/**
 * UsersTemplate - Pure UI component for Users page
 *
 * This is a presentational component that receives all data and handlers as props.
 * Contains NO business logic, NO data fetching, NO state management.
 */

import { DataTable, ColumnDef } from '@/components/shared/DataTable/DataTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert } from '@/components/ui/alert'
import { AlertCircle, Users as UsersIcon } from 'lucide-react'
import { User } from '@/features/users/api'
import { TopUpDialog } from '@/features/users/components/TopUpDialog'
import { RefillSettingsDialog } from '@/features/users/components/RefillSettingsDialog'
import { UserConfigModal } from '@/features/users/components/UserConfigModal'

export interface UsersTemplateProps {
  // Data
  users: User[]
  total: number
  page: number
  limit: number
  totalPages: number
  isLoading: boolean
  error: Error | null

  // Modals state
  topUpUser: User | null
  refillSettingsUser: User | null
  configUser: User | null
  onSetTopUpUser: (user: User | null) => void
  onSetRefillSettingsUser: (user: User | null) => void
  onSetConfigUser: (user: User | null) => void

  // Table
  columns: ColumnDef<User>[]
  onPageChange: (page: number) => void

  // Handlers
  onRefresh: () => void
}

export function UsersTemplate({
  users,
  total,
  page,
  limit,
  totalPages,
  isLoading,
  error,
  topUpUser,
  refillSettingsUser,
  configUser,
  onSetTopUpUser,
  onSetRefillSettingsUser,
  onSetConfigUser,
  columns,
  onPageChange,
  onRefresh
}: UsersTemplateProps) {
  return (
    <>
      {/* Stats Card */}
      {total > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Overview</CardTitle>
            <CardDescription>User statistics from SAIFChat database</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{total}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Total Agents</p>
                <p className="text-2xl font-bold">
                  {users.reduce((sum, user) => sum + (user.total_agents || 0), 0)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Current Page</p>
                <p className="text-2xl font-bold">{page} / {totalPages || 1}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Showing</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <div className="ml-2">
            <h4 className="font-medium">Error loading users</h4>
            <p className="text-sm mt-1">
              {error instanceof Error ? error.message : 'An unknown error occurred'}
            </p>
          </div>
        </Alert>
      )}

      {/* User Table */}
      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        emptyMessage="No users found"
        emptyIcon={<UsersIcon className="h-12 w-12 text-muted-foreground" />}
        pagination={{
          currentPage: page,
          totalItems: total,
          pageSize: limit,
          onPageChange: onPageChange,
        }}
      />

      {/* Modals */}
      {topUpUser && (
        <TopUpDialog
          user={topUpUser}
          onClose={() => {
            onSetTopUpUser(null)
            onRefresh()
          }}
        />
      )}

      {refillSettingsUser && (
        <RefillSettingsDialog
          user={refillSettingsUser}
          onClose={() => {
            onSetRefillSettingsUser(null)
            onRefresh()
          }}
        />
      )}

      <UserConfigModal
        user={configUser}
        open={!!configUser}
        onClose={() => onSetConfigUser(null)}
      />
    </>
  )
}
